// src/pages/Aanvragen.js
// vAAN-031
// - Start vanuit Opdrachten hersteld: ?new=1&order_id=... opent direct een nieuwe Aanvraag en neemt Opdracht-defaults over
// - Fallback via staffiq_source_hrm_order_id toegevoegd
// vAAN-030
// - Opdracht toegevoegd boven Relatie; keuze vult lege aanvraagvelden standaard vanuit HRM Opdrachten
// - Bestaande handmatig ingevulde aanvraaggegevens blijven staan wanneer Opdracht later wordt gekozen
// - Opdracht toegevoegd aan grid tussen Status en CAO
// vAAN-029
// - Rubriek Plaats verwijderd uit rechterkolom; Regio blijft voor matching
// vAAN-028
// - Locatie/bezoekadres uitgebreid; bezoekadres relatie automatisch ingevuld bij lege locatie
// - Contact op locatie en Aanmeldprocedure toegevoegd
// - Opzegtermijn toegevoegd onder Einddatum en opgenomen in Aanvraag -> Vacature -> Plaatsing
// vAAN-026
// - Reisuren label gewijzigd naar Reisuren per dag
// - Reiskosten uitgebreid met keuze per km / per dag en doorgegeven in de HRM-keten
// vAAN-025
// - Reisuren toegevoegd en opgenomen in Aanvraag -> Vacature/Matching -> Plaatsing
// vAAN-024
// - Labels: Aanvraagnummer -> Aanvraagreferentie; Aanvraag -> Korte omschrijving
// - Optionele route toegevoegd: Aanvraag -> Vacature -> Matching -> Plaatsing
// - Directe route Aanvraag -> Matching -> Plaatsing blijft beschikbaar
// vAAN-022
// - Duidelijke foutmelding wanneer geen inzetvorm is geselecteerd
// vAAN-021
// - Inzetvorm toegevoegd: Vast / Tijdelijk / ZZP / Doorleen
// - Meerdere inzetvormen tegelijk toegestaan; minimaal één verplicht
// - Standaard staan alle vier aan
// - Vervoer uitgebreid met Eigen / Vd Zaak; beide keuzes sluiten elkaar uit
// vAAN-020
// - Functieprofiel importeren toegevoegd met AI-analyse en automatische veldvulling
// - Smartphone: na selectie van een regel wordt de invoerkolom bovenaan in beeld gezet
// - Verkooptarief wordt automatisch berekend uit Factor x Salarisindicatie, maar blijft handmatig aanpasbaar
//
// StaffIQ HRM - Aanvragen
// Opzet gelijk aan Personeel / Matching:
// - Links/boven: zoeken + knoppen
// - Midden: overzicht met sortering en kolomfilters
// - Rechts: detailkaart

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vAAN-031";

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function profilePick(data, key) {
  const value = data?.[key];
  if (value === null || value === undefined) return "";
  return safeTrim(value);
}

function normalizeProfileDate(value) {
  const raw = safeTrim(value);
  if (!raw) return "";

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const nl = raw.match(/^(\d{1,2})[-/.\s](\d{1,2})[-/.\s](\d{4})$/);
  if (nl) {
    const dd = String(nl[1]).padStart(2, "0");
    const mm = String(nl[2]).padStart(2, "0");
    return `${nl[3]}-${mm}-${dd}`;
  }

  return "";
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

function uniqSorted(arr) {
  const set = new Set((arr || []).map((x) => String(x).trim()).filter(Boolean));
  return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatDateNL(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

function parseDecimal(v) {
  const raw = String(v ?? "")
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return 0;

  let cleaned = raw;

  if (cleaned.includes(",")) {
    // Nederlandse invoer: 2,35 of 2.350,75
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else {
    // Engelse/database-notatie: 2.35 moet 2.35 blijven.
    // Alleen meerdere punten zien we als duizendtallen: 2.350.750 -> 2350750
    const dotCount = (cleaned.match(/\./g) || []).length;
    if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  }

  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function decimalInput(v, decimals = 2) {
  if (v === null || v === undefined || v === "") return "";

  const n = parseDecimal(v);
  if (!Number.isFinite(n)) return "";

  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function cleanDecimalInputValue(v) {
  return String(v || "").replace(/[^0-9,.-]/g, "");
}

function formatDecimalInputValue(v, decimals = 2) {
  const raw = String(v || "").trim();
  if (!raw) return "";
  const n = parseDecimal(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toLocaleString("nl-NL", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function parseActiveValue(it) {
  const raw = it?.active ?? it?.is_active ?? it?.isActive ?? it?.actief;
  if (raw === undefined || raw === null || raw === "") return null;
  if (raw === true) return true;
  if (raw === false) return false;
  const s = String(raw).toLowerCase().trim();
  if (raw === 1 || s === "1" || s === "true" || s === "yes" || s === "y" || s === "j" || s === "ja" || s === "aan" || s === "on") return true;
  if (raw === 0 || s === "0" || s === "false" || s === "no" || s === "n" || s === "nee" || s === "uit" || s === "off") return false;
  return !!raw;
}

function boolToJaNee(v) {
  return v === true ? "j" : "n";
}

function getSelectedCompanyCodeIdsHeader() {
  return (
    sessionStorage.getItem("selectedCompanyCodeIds") ||
    localStorage.getItem("selectedCompanyCodeIds") ||
    sessionStorage.getItem("company_code_ids") ||
    localStorage.getItem("company_code_ids") ||
    ""
  );
}

function companyCodeDisplayName(row = {}) {
  return safeTrim(row.company_code_name || row.company_code || row.company_code_display || "");
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let role = "user";
  try {
    role =
      JSON.parse(
        sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
      )?.role || "user";
  } catch {}

  let lastErr = null;

  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-company-code-ids": getSelectedCompanyCodeIdsHeader() || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), {
        ...options,
        headers,
      });

      if (!res.ok) {
        const raw = await res.text();
        let msg = `${url} (${res.status})`;

        try {
          const data = raw ? JSON.parse(raw) : null;
          msg = data?.error || data?.message || raw || msg;
        } catch {
          msg = raw || msg;
        }

        throw new Error(msg);
      }

      if (res.status === 204) return null;
      const text = await res.text();
      if (!text) return null;

      try {
        return JSON.parse(text);
      } catch {
        return text;
      }
    } catch (err) {
      lastErr = err;
    }
  }

  throw lastErr;
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function emptyFormToday() {
  return {
    id: null,
    application_date: new Date().toISOString().slice(0, 10),
    status: "Open",
    application_number: "",
    order_id: "",
    organisation_id: "",
    contact_id: "",
    company_code_id: "",
    company_code: "",
    company_code_name: "",
    title: "",
    job_title: "",
    number_of_people: "",
    work_description: "",
    start_date: "",
    end_date: "",
    notice_period: "",
    hours_per_week: "",
    schedule_text: "",
    location: "",
    location_contact: "",
    registration_procedure: "",
    place: "",
    region: "",
    education: "",
    experience: "",
    certificates: "",
    driving_license: "",
    own_transport: "",
    company_transport: false,
    employment_vast: true,
    employment_temporary: true,
    employment_zzp: true,
    employment_doorleen: true,
    languages: "",
    matching_text: "",
    cao: "",
    wage_period: "week",
    invoice_period: "week",
    factor: "",
    rate_indication: "",
    sales_rate: "",
    min_rate: "",
    travel_costs: "",
    travel_costs_unit: "km",
    travel_hours_amount: "",
    allowances: "",
    invoice_agreement: "",
    priority: "Normaal",
    request_source: "",
    proposal_deadline: "",
    internal_note: "",
    external_note: "",
    remarks: "",
    active: true,
  };
}


function organisationVisitAddressText(org = {}) {
  const name = safeTrim(org?.name || org?.naam || org?.bedrijfsnaam || org?.search_name || org?.zoeknaam || "");
  const street = safeTrim(org?.visit_street || org?.bezoek_straat || "");
  const houseNumber = safeTrim(org?.visit_house_number || org?.bezoek_huisnummer || "");
  const addition = safeTrim(org?.visit_addition || org?.bezoek_toevoeging || "");
  const postcode = safeTrim(org?.visit_postcode || org?.bezoek_postcode || "");
  const city = safeTrim(org?.visit_city || org?.bezoek_plaats || "");
  const country = safeTrim(org?.visit_country || org?.bezoek_land || "");
  const streetLine = [street, houseNumber, addition].filter(Boolean).join(" ");
  const cityLine = [postcode, city].filter(Boolean).join(" ");
  return [name, streetLine, cityLine, country].filter(Boolean).join("\n");
}

function organisationVisitPlace(org = {}) {
  return safeTrim(org?.visit_city || org?.bezoek_plaats || "");
}

function organisationVisitRegion(org = {}) {
  return safeTrim(org?.visit_region || org?.region || org?.regio || "");
}

function pickOrgName(o) {
  return safeTrim(o?.search_name || o?.zoeknaam || o?.name || o?.naam || o?.bedrijfsnaam || "");
}

function orgIdOf(o) {
  return o?.id ?? o?.organisation_id ?? o?.organisatie_id ?? "";
}

function orgNameFromList(list, id, fallback = "") {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return pickOrgName(found) || fallback || "";
}

function pickContactName(c) {
  const fn = safeTrim(c?.first_name || c?.voornaam || "");
  const ln = safeTrim(c?.last_name || c?.achternaam || "");
  const full = safeTrim(c?.full_name || c?.naam || "");
  return safeTrim(c?.search_name || c?.display_name || (ln && fn ? `${ln}, ${fn}` : "") || full || fn || "");
}

function contactIdOf(c) {
  return c?.id ?? c?.contact_id ?? "";
}

function orgCompanyCodeIdFromList(list, id) {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return found?.company_code_id ?? "";
}

function orgCompanyCodeNameFromList(list, id) {
  const wanted = String(id || "");
  const found = (list || []).find((o) => String(orgIdOf(o)) === wanted);
  return companyCodeDisplayName(found);
}

function applicationCompanyCodeName(item = {}, organisationsList = []) {
  return (
    companyCodeDisplayName(item) ||
    orgCompanyCodeNameFromList(organisationsList, item?.organisation_id) ||
    ""
  );
}

function normalizeApplication(raw = {}) {
  return {
    ...raw,
    id: raw.id ?? null,
    application_date: isoDate(raw.application_date),
    start_date: isoDate(raw.start_date),
    end_date: isoDate(raw.end_date),
    notice_period: raw.notice_period ?? "",
    location_contact: raw.location_contact ?? "",
    registration_procedure: raw.registration_procedure ?? "",
    proposal_deadline: isoDate(raw.proposal_deadline),
    order_id: raw.order_id ?? raw.hrm_order_id ?? "",
    organisation_id: raw.organisation_id ?? "",
    contact_id: raw.contact_id ?? "",
    company_code_id: raw.company_code_id ?? "",
    company_code: raw.company_code ?? "",
    company_code_name: raw.company_code_name ?? "",
    number_of_people: raw.number_of_people ?? "",
    hours_per_week: decimalInput(raw.hours_per_week, 2),
    factor: decimalInput(raw.factor, 2),
    rate_indication: decimalInput(raw.rate_indication, 2),
    sales_rate: decimalInput(raw.sales_rate, 2),
    min_rate: decimalInput(raw.min_rate ?? raw.max_rate, 2),
    company_transport: raw.company_transport === true,
    employment_vast: raw.employment_vast !== false,
    employment_temporary: raw.employment_temporary !== false,
    employment_zzp: raw.employment_zzp !== false,
    employment_doorleen: raw.employment_doorleen !== false,
    travel_costs: decimalInput(raw.travel_costs, 2),
    travel_costs_unit: raw.travel_costs_unit === "day" ? "day" : "km",
    travel_hours_amount: decimalInput(raw.travel_hours_amount, 2),
    active: parseActiveValue(raw) !== false,
  };
}

export default function Aanvragen() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonlyRole = String(role || "").toLowerCase() === "readonly";

  const [items, setItems] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyFormToday());
  const [listIndex, setListIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");

  // Grote editor voor Opmerking intern / extern.
  // Waarde is "" | "internal_note" | "external_note".
  const [noteEditor, setNoteEditor] = useState("");

  const [profileImportBusy, setProfileImportBusy] = useState(false);
  const [profileImportMessage, setProfileImportMessage] = useState("");
  const [profileImportResultOpen, setProfileImportResultOpen] = useState(false);
  const [profileImportError, setProfileImportError] = useState("");
  const profileFileInputRef = useRef(null);

  const [q, setQ] = useState(getParam("q"));
  const [organisationFilterId, setOrganisationFilterId] = useState(getParam("organisation_id"));

  const EMPTY_VAL = "__EMPTY__";
  const [dateFilter, setDateFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [functionFilter, setFunctionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderFilter, setOrderFilter] = useState("");
  const [caoFilter, setCaoFilter] = useState("");
  const [wagePeriodFilter, setWagePeriodFilter] = useState("");
  const [invoicePeriodFilter, setInvoicePeriodFilter] = useState("");
  const [factorFilter, setFactorFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("j");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

  const [mode, setMode] = useState("search");
  const isFormLocked = isReadonlyRole || mode !== "form";
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  const pageTopRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const reopenHandledRef = useRef(false);
  const orderLaunchHandledRef = useRef(false);
  const rightScrollRef = useRef(null);

  const dateRef = useRef(null);
  const numberRef = useRef(null);
  const orderRef = useRef(null);
  const relationRef = useRef(null);
  const contactRef = useRef(null);
  const titleRef = useRef(null);
  const functionRef = useRef(null);
  const statusRef = useRef(null);
  const peopleRef = useRef(null);
  const workDescriptionRef = useRef(null);
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);
  const noticePeriodRef = useRef(null);
  const hoursRef = useRef(null);
  const scheduleRef = useRef(null);
  const locationRef = useRef(null);
  const locationContactRef = useRef(null);
  const registrationProcedureRef = useRef(null);
  const regionRef = useRef(null);
  const educationRef = useRef(null);
  const experienceRef = useRef(null);
  const certificatesRef = useRef(null);
  const drivingLicenseRef = useRef(null);
  const ownTransportRef = useRef(null);
  const languagesRef = useRef(null);
  const matchingTextRef = useRef(null);
  const caoRef = useRef(null);
  const wagePeriodRef = useRef(null);
  const invoicePeriodRef = useRef(null);
  const factorRef = useRef(null);
  const rateIndicationRef = useRef(null);
  const salesRateRef = useRef(null);
  const minRateRef = useRef(null);
  const travelCostsRef = useRef(null);
  const travelCostsUnitRef = useRef(null);
  const travelHoursAmountRef = useRef(null);
  const allowancesRef = useRef(null);
  const invoiceAgreementRef = useRef(null);
  const priorityRef = useRef(null);
  const sourceRef = useRef(null);
  const proposalDeadlineRef = useRef(null);
  const internalNoteRef = useRef(null);
  const externalNoteRef = useRef(null);
  const internalNoteBigRef = useRef(null);
  const externalNoteBigRef = useRef(null);
  const ignoreNextNoteFocusRef = useRef("");

  function openNoteEditor(key) {
    if (isFormLocked) return;

    setNoteEditor(key);

    setTimeout(() => {
      try {
        const ref = key === "internal_note" ? internalNoteBigRef : externalNoteBigRef;
        const el = ref.current;
        if (!el) return;

        el.focus?.({ preventScroll: true });
        const len = String(el.value || "").length;
        el.setSelectionRange?.(len, len);
      } catch {}
    }, 0);
  }

  function closeNoteEditor() {
    const key = noteEditor;

    // Voorkom dat de focus op het kleine veld de editor direct weer opent.
    ignoreNextNoteFocusRef.current = key;
    setNoteEditor("");

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          const panel = rightScrollRef.current;
          const ref = key === "internal_note" ? internalNoteRef : externalNoteRef;
          const el = ref.current;

          if (!el) return;

          // Cursor terug in het betreffende kleine veld.
          try {
            el.focus?.({ preventScroll: true });
          } catch {
            el.focus?.();
          }

          // Zet het betreffende veld onderaan in de rechterkolom.
          if (panel && window.innerWidth > 768) {
            const panelRect = panel.getBoundingClientRect();
            const elRect = el.getBoundingClientRect();
            const currentTop = panel.scrollTop;
            const relativeBottom = elRect.bottom - panelRect.top + currentTop;
            const paddingBottom = 10;

            panel.scrollTop = Math.max(
              0,
              relativeBottom - panel.clientHeight + paddingBottom
            );
          } else {
            // Mobiel: veld zelf netjes in beeld brengen.
            el.scrollIntoView?.({
              behavior: "auto",
              block: "end",
              inline: "nearest",
            });
          }
        } catch {}
      });
    });
  }

  const focusOrderRefs = [
    dateRef,
    numberRef,
    orderRef,
    relationRef,
    contactRef,
    titleRef,
    functionRef,
    statusRef,
    peopleRef,
    workDescriptionRef,
    startDateRef,
    endDateRef,
    noticePeriodRef,
    hoursRef,
    scheduleRef,
    locationRef,
    locationContactRef,
    registrationProcedureRef,
    regionRef,
    educationRef,
    experienceRef,
    certificatesRef,
    drivingLicenseRef,
    ownTransportRef,
    languagesRef,
    matchingTextRef,
    caoRef,
    wagePeriodRef,
    invoicePeriodRef,
    factorRef,
    rateIndicationRef,
    salesRateRef,
    minRateRef,
    travelCostsRef,
    travelCostsUnitRef,
    travelHoursAmountRef,
    allowancesRef,
    invoiceAgreementRef,
    priorityRef,
    sourceRef,
    proposalDeadlineRef,
    internalNoteRef,
    externalNoteRef,
  ];

  async function loadOrders() {
    try {
      const data = await tryFetchJson(["/api/hrm-orders"]);
      setOrders(Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    }
  }

  function orderReference(item = {}) {
    const wanted = String(item?.order_id ?? item?.hrm_order_id ?? "");
    const found = (orders || []).find((o) => String(o?.id ?? "") === wanted);
    return safeTrim(found?.order_reference || item?.order_reference || "");
  }

  function applyOrderDefaults(orderId) {
    const selectedOrder = (orders || []).find((o) => String(o?.id ?? "") === String(orderId || ""));

    setForm((prev) => {
      if (!selectedOrder) return { ...prev, order_id: orderId || "" };

      const organisationId = selectedOrder.organisation_id ? String(selectedOrder.organisation_id) : "";
      const orderOrg = (organisations || []).find((o) => String(orgIdOf(o)) === organisationId);

      // Opdracht levert alleen defaults. Wat al in de Aanvraag staat, blijft bewust staan.
      return {
        ...prev,
        order_id: String(selectedOrder.id || ""),
        organisation_id: safeTrim(prev.organisation_id) || organisationId,
        contact_id: safeTrim(prev.contact_id) || (selectedOrder.contact_id ? String(selectedOrder.contact_id) : ""),
        company_code_id: safeTrim(prev.company_code_id) || (selectedOrder.company_code_id ? String(selectedOrder.company_code_id) : "") || orgCompanyCodeIdFromList(organisations, organisationId),
        company_code_name: safeTrim(prev.company_code_name) || safeTrim(selectedOrder.company_code_name) || orgCompanyCodeNameFromList(organisations, organisationId),
        location: safeTrim(prev.location) || safeTrim(selectedOrder.location) || organisationVisitAddressText(orderOrg),
        place: safeTrim(prev.place) || organisationVisitPlace(orderOrg),
        region: safeTrim(prev.region) || organisationVisitRegion(orderOrg),
        start_date: safeTrim(prev.start_date) || isoDate(selectedOrder.start_date),
        end_date: safeTrim(prev.end_date) || isoDate(selectedOrder.end_date),
        cao: safeTrim(prev.cao) || safeTrim(selectedOrder.cao),
      };
    });
  }

  async function loadOrganisations() {
    try {
      const data = await tryFetchJson(["/api/organisations"]);
      setOrganisations(Array.isArray(data) ? data : []);
    } catch {
      setOrganisations([]);
    }
  }

  async function loadContacts(organisationId) {
    const id = safeTrim(organisationId);
    if (!id) {
      setContacts([]);
      return;
    }

    try {
      const data = await tryFetchJson([`/api/contacts?organisation_id=${encodeURIComponent(id)}`]);
      setContacts(Array.isArray(data) ? data : []);
    } catch {
      setContacts([]);
    }
  }

  async function loadList(keepSelection = true) {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (safeTrim(q)) params.set("q", safeTrim(q));
      const data = await tryFetchJson([`/api/applications?${params.toString()}`, `/api/aanvragen?${params.toString()}`]);
      const rows = Array.isArray(data) ? data.map(normalizeApplication) : [];

      setItems(rows);

      if (keepSelection && selectedId) {
        const idx = rows.findIndex((x) => String(x.id) === String(selectedId));
        if (idx >= 0) {
          setListIndex(idx);
          return rows;
        }
      }

      setListIndex(rows.length ? 0 : -1);
      return rows;
    } catch (e) {
      setError(e?.message || String(e));
      setItems([]);
      setListIndex(-1);
      return [];
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadOrders();
    loadOrganisations();
  }, []);

  useEffect(() => {
    loadContacts(form.organisation_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.organisation_id]);

  useEffect(() => {
    loadList(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start rechtstreeks vanuit HRM Opdrachten.
  // Opdrachten stuurt ?new=1&order_id=<id> mee en bewaart daarnaast
  // staffiq_source_hrm_order_id als extra zekerheid.
  useEffect(() => {
    if (orderLaunchHandledRef.current) return;
    if (!orders.length || !organisations.length) return;

    const wantsNew = getParam("new") === "1";
    let orderId = safeTrim(getParam("order_id"));

    if (!orderId) {
      try {
        orderId = safeTrim(sessionStorage.getItem("staffiq_source_hrm_order_id") || "");
      } catch {}
    }

    if (!wantsNew || !orderId) {
      orderLaunchHandledRef.current = true;
      return;
    }

    const selectedOrder = (orders || []).find(
      (o) => String(o?.id ?? "") === String(orderId)
    );

    // De orderlijst kan iets later binnenkomen; zolang de order nog niet
    // gevonden is, wachten we één rendercyclus.
    if (!selectedOrder) return;

    orderLaunchHandledRef.current = true;
    setSelectedId(null);
    setForm(emptyFormToday());
    setMode("form");
    setNoteEditor("");
    setSaveError(null);

    requestAnimationFrame(() => {
      applyOrderDefaults(orderId);

      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
      } catch {}

      try {
        orderRef.current?.focus?.({ preventScroll: true });
      } catch {
        orderRef.current?.focus?.();
      }
    });

    try {
      sessionStorage.removeItem("staffiq_source_hrm_order_id");
    } catch {}

    try {
      window.history.replaceState({}, "", window.location.pathname);
    } catch {}
  }, [orders, organisations]);

  function relationName(item) {
    return orgNameFromList(organisations, item?.organisation_id, item?.organisation_name || "");
  }

  function companyCodeName(item) {
    return applicationCompanyCodeName(item, organisations);
  }

  function rowText(item) {
    return [
      item?.application_date,
      item?.application_number,
      relationName(item),
      item?.title,
      item?.job_title,
      item?.status,
      orderReference(item),
      item?.number_of_people,
      item?.work_description,
      item?.start_date,
      item?.end_date,
      item?.notice_period,
      item?.hours_per_week,
      item?.schedule_text,
      item?.location,
      item?.location_contact,
      item?.registration_procedure,
      item?.place,
      item?.region,
      item?.education,
      item?.experience,
      item?.certificates,
      item?.driving_license,
      item?.own_transport,
      item?.languages,
      item?.matching_text,
      item?.cao,
      item?.wage_period,
      item?.invoice_period,
      item?.factor,
      item?.rate_indication,
      item?.sales_rate,
      item?.min_rate ?? item?.max_rate,
      item?.travel_costs,
      item?.travel_costs_unit,
      item?.travel_hours_amount,
      item?.allowances,
      item?.invoice_agreement,
      item?.priority,
      item?.request_source,
      item?.proposal_deadline,
      item?.internal_note,
      item?.external_note,
      item?.remarks,
      companyCodeName(item),
      boolToJaNee(parseActiveValue(item) !== false),
    ].join(" ");
  }

  function filterBySelect(value, filter) {
    if (!filter) return true;
    const v = safeTrim(value);
    if (filter === EMPTY_VAL) return !v;
    return norm(v) === norm(filter);
  }

  const filtered = useMemo(() => {
    const query = norm(q);

    return (items || []).filter((item) => {
      if (
        organisationFilterId &&
        String(item.organisation_id || "") !== String(organisationFilterId)
      ) {
        return false;
      }

      if (query && !norm(rowText(item)).includes(query)) return false;
      if (!filterBySelect(formatDateNL(item.application_date), dateFilter)) return false;
      if (!filterBySelect(relationName(item), relationFilter)) return false;
      if (!filterBySelect(item.title, titleFilter)) return false;
      if (!filterBySelect(item.job_title, functionFilter)) return false;
      if (!filterBySelect(item.status, statusFilter)) return false;
      if (!filterBySelect(orderReference(item), orderFilter)) return false;
      if (!filterBySelect(item.cao, caoFilter)) return false;
      if (!filterBySelect(item.wage_period, wagePeriodFilter)) return false;
      if (!filterBySelect(item.invoice_period, invoicePeriodFilter)) return false;
      if (!filterBySelect(decimalInput(item.factor), factorFilter)) return false;
      if (!filterBySelect(companyCodeName(item), companyCodeFilter)) return false;

      const activeBool = parseActiveValue(item) !== false;
      if (activeFilter === "j" && !activeBool) return false;
      if (activeFilter === "n" && activeBool) return false;

      return true;
    });
  }, [items, q, dateFilter, relationFilter, titleFilter, functionFilter, statusFilter, orderFilter, caoFilter, wagePeriodFilter, invoicePeriodFilter, factorFilter, companyCodeFilter, activeFilter, organisations, orders, organisationFilterId]);

  const sorted = useMemo(() => {
    const arr = filtered.slice();
    const dir = sort.dir === "desc" ? -1 : 1;

    arr.sort((a, b) => {
      function val(x) {
        if (sort.key === "date") return x.application_date || "";
        if (sort.key === "relation") return relationName(x);
        if (sort.key === "title") return x.title || "";
        if (sort.key === "function") return x.job_title || "";
        if (sort.key === "status") return x.status || "";
        if (sort.key === "order") return orderReference(x);
        if (sort.key === "cao") return x.cao || "";
        if (sort.key === "wage") return x.wage_period || "";
        if (sort.key === "invoice") return x.invoice_period || "";
        if (sort.key === "factor") return Number(String(x.factor || "").replace(",", ".")) || 0;
        if (sort.key === "active") return parseActiveValue(x) !== false ? "j" : "n";
        if (sort.key === "companyCode") return companyCodeName(x);
        return "";
      }

      const av = val(a);
      const bv = val(b);

      if (typeof av === "number" || typeof bv === "number") {
        return ((av || 0) - (bv || 0)) * dir;
      }

      return String(av).localeCompare(String(bv), "nl", { numeric: true }) * dir;
    });

    return arr;
  }, [filtered, sort, organisations, orders]);

  const gridCount = sorted.length > 9999 ? "9999+" : sorted.length;

  const dateOptions = uniqSorted(items.map((x) => formatDateNL(x.application_date)));
  const relationOptions = uniqSorted(items.map((x) => relationName(x)));
  const titleOptions = uniqSorted(items.map((x) => x.title));
  const functionOptions = uniqSorted(items.map((x) => x.job_title));
  const statusOptions = uniqSorted(items.map((x) => x.status));
  const orderOptions = uniqSorted(items.map((x) => orderReference(x)));
  const caoOptions = uniqSorted(items.map((x) => x.cao));
  const wageOptions = uniqSorted(items.map((x) => x.wage_period));
  const invoiceOptions = uniqSorted(items.map((x) => x.invoice_period));
  const factorOptions = uniqSorted(items.map((x) => decimalInput(x.factor)));
  const companyCodeOptions = uniqSorted(items.map((x) => companyCodeName(x)));

  useEffect(() => {
    if (loading || reopenHandledRef.current || !items.length) return;

    let requestedId = "";
    try {
      requestedId = String(
        sessionStorage.getItem("staffiq_reopen_application_id") || ""
      ).trim();
    } catch {}

    if (!requestedId) {
      reopenHandledRef.current = true;
      return;
    }

    const row = items.find(
      (item) => String(item.id) === String(requestedId)
    );

    if (!row) return;

    reopenHandledRef.current = true;

    try {
      sessionStorage.removeItem("staffiq_reopen_application_id");
    } catch {}

    const visibleIndex = sorted.findIndex(
      (item) => String(item.id) === String(requestedId)
    );

    setListIndex(visibleIndex >= 0 ? visibleIndex : 0);
    loadItem(row);
  }, [loading, items, sorted]);

  async function loadItem(row) {
    if (!row?.id) return;

    try {
      setSaveError(null);
      const data = await tryFetchJson([`/api/applications/${row.id}`, `/api/aanvragen/${row.id}`]);
      const item = normalizeApplication(data);
      setSelectedId(item.id);
      setForm({ ...emptyFormToday(), ...item });
      setMode("form");
      setNoteEditor("");

      setTimeout(() => {
        try {
          const panel = rightScrollRef.current;
          if (!panel) return;

          // Altijd eerst de invoerkolom intern naar de eerste rubriek.
          panel.scrollTop = 0;

          if (window.innerWidth <= 768) {
            // Smartphone: zet de volledige invoerkolom bovenaan in het scherm.
            // Geen automatische focus, zodat het toetsenbord dicht blijft.
            panel.scrollIntoView?.({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });

            if (
              document.activeElement &&
              typeof document.activeElement.blur === "function"
            ) {
              document.activeElement.blur();
            }

            return;
          }

          // Desktop: cursor op de eerste rubriek.
          dateRef.current?.focus?.();
        } catch {}
      }, 120);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function newItem() {
    if (isReadonlyRole) return;
    setSelectedId(null);
    setForm(emptyFormToday());
    setMode("form");
    setNoteEditor("");
    setSaveError(null);
    requestAnimationFrame(() => {
      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        dateRef.current?.focus?.();
      } catch {}
    });
  }

  function vernieuwList() {
    setMode("search");
    setNoteEditor("");
    setOrganisationFilterId("");
    setSelectedId(null);
    setForm(emptyFormToday());
    setSaveError(null);
    setProfileImportBusy(false);
    setProfileImportMessage("");
    setProfileImportResultOpen(false);
    setProfileImportError("");
    loadList(false);
  }

  function applyProfileImport(data = {}) {
    const fields = data?.fields || data?.application || data || {};
    const requirements = Array.isArray(data?.requirements) ? data.requirements : [];
    const responsibilities = Array.isArray(data?.responsibilities) ? data.responsibilities : [];
    const competencies = Array.isArray(data?.competencies) ? data.competencies : [];

    setForm((prev) => {
      const next = { ...prev };

      function setIfValue(key, value, transform = (x) => x) {
        const raw = safeTrim(value);
        if (!raw) return;
        next[key] = transform(raw);
      }

      setIfValue("job_title", fields.job_title);
      setIfValue("title", fields.title);
      setIfValue("number_of_people", fields.number_of_people);
      setIfValue("work_description", fields.work_description);
      setIfValue("start_date", fields.start_date, normalizeProfileDate);
      setIfValue("end_date", fields.end_date, normalizeProfileDate);
      setIfValue("notice_period", fields.notice_period);
      setIfValue("hours_per_week", fields.hours_per_week, (x) => formatDecimalInputValue(x, 2));
      setIfValue("schedule_text", fields.schedule_text);
      setIfValue("location", fields.location);
      setIfValue("place", fields.place);
      setIfValue("region", fields.region);
      setIfValue("education", fields.education);
      setIfValue("experience", fields.experience);
      setIfValue("certificates", fields.certificates);
      setIfValue("driving_license", fields.driving_license);
      if (fields.company_transport !== undefined && fields.company_transport !== null) {
        const companyValue = String(fields.company_transport).trim().toLowerCase();
        const isCompanyTransport = ["true", "1", "ja", "j", "yes", "y", "van de zaak", "vd zaak"].includes(companyValue);
        next.company_transport = isCompanyTransport;
        if (isCompanyTransport) next.own_transport = "";
      }

      if (fields.own_transport !== undefined && fields.own_transport !== null) {
        const ownValue = String(fields.own_transport).trim().toLowerCase();
        const isOwnTransport = ["true", "1", "ja", "j", "yes", "y", "eigen", "eigen vervoer", "gewenst"].includes(ownValue);
        if (isOwnTransport) {
          next.own_transport = "Ja";
          next.company_transport = false;
        }
      }
      setIfValue("languages", fields.languages);
      setIfValue("matching_text", fields.matching_text);
      setIfValue("cao", fields.cao);
      setIfValue("wage_period", fields.wage_period);
      setIfValue("invoice_period", fields.invoice_period);
      setIfValue("factor", fields.factor, (x) => formatDecimalInputValue(x, 2));
      setIfValue("rate_indication", fields.rate_indication, (x) => formatDecimalInputValue(x, 2));
      setIfValue("sales_rate", fields.sales_rate, (x) => formatDecimalInputValue(x, 2));
      setIfValue("min_rate", fields.min_rate, (x) => formatDecimalInputValue(x, 2));
      setIfValue("travel_costs", fields.travel_costs, (x) => formatDecimalInputValue(x, 2));
      setIfValue("travel_hours_amount", fields.travel_hours_amount, (x) => formatDecimalInputValue(x, 2));
      setIfValue("allowances", fields.allowances);
      setIfValue("invoice_agreement", fields.invoice_agreement);
      setIfValue("priority", fields.priority);
      setIfValue("request_source", fields.request_source);
      setIfValue("proposal_deadline", fields.proposal_deadline, normalizeProfileDate);
      setIfValue("internal_note", fields.internal_note);
      setIfValue("external_note", fields.external_note);

      const additions = [];

      if (safeTrim(data?.profile_summary)) {
        additions.push(`AI-samenvatting functieprofiel:\n${safeTrim(data.profile_summary)}`);
      }
      if (requirements.length) {
        additions.push(`Eisen:\n${requirements.map((x) => `- ${safeTrim(x)}`).filter((x) => x !== "- ").join("\n")}`);
      }
      if (responsibilities.length) {
        additions.push(`Werkzaamheden / verantwoordelijkheden:\n${responsibilities.map((x) => `- ${safeTrim(x)}`).filter((x) => x !== "- ").join("\n")}`);
      }
      if (competencies.length) {
        additions.push(`Competenties:\n${competencies.map((x) => `- ${safeTrim(x)}`).filter((x) => x !== "- ").join("\n")}`);
      }

      if (additions.length) {
        const extra = additions.join("\n\n");
        next.matching_text = safeTrim(next.matching_text)
          ? `${safeTrim(next.matching_text)}\n\n${extra}`
          : extra;
      }

      return next;
    });

    const filled = [
      fields.job_title ? "functie" : "",
      fields.work_description ? "werkzaamheden" : "",
      fields.hours_per_week ? "uren" : "",
      fields.place || fields.region ? "locatie" : "",
      fields.education || fields.experience ? "profieleisen" : "",
      fields.matching_text || data?.profile_summary ? "matchingtekst" : "",
    ].filter(Boolean);

    setProfileImportMessage(
      filled.length
        ? `Functieprofiel geanalyseerd. Ingevuld: ${filled.join(", ")}. Controleer alle gegevens en klik daarna zelf op Opslaan.`
        : "Functieprofiel geanalyseerd. Er zijn geen duidelijke velden gevonden. Controleer het document en vul de aanvraag handmatig aan."
    );
    setProfileImportResultOpen(true);
    setMode("form");

    setTimeout(() => {
      try {
        rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
        dateRef.current?.focus?.();
        dateRef.current?.select?.();
      } catch {}
    }, 100);
  }

  async function handleProfileImportFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setProfileImportBusy(true);
      setProfileImportError("");
      setProfileImportMessage("");
      setProfileImportResultOpen(false);

      const token =
        sessionStorage.getItem("token") ||
        localStorage.getItem("token") ||
        "";

      const tenant =
        sessionStorage.getItem("tenant") ||
        localStorage.getItem("tenant") ||
        "demo";

      let parsedUser = {};
      try {
        parsedUser = JSON.parse(
          sessionStorage.getItem("user") ||
          localStorage.getItem("user") ||
          "{}"
        );
      } catch {}

      const data = new FormData();
      data.append("file", file);

      const headers = {
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": parsedUser?.role || "user",
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": getSelectedCompanyCodeIdsHeader() || undefined,
      };

      Object.keys(headers).forEach(
        (key) => headers[key] === undefined && delete headers[key]
      );

      const response = await fetch(buildUrl("/api/ai/applications/profile-extract"), {
        method: "POST",
        headers,
        body: data,
      });

      const json = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          json?.error ||
          json?.message ||
          "Functieprofiel-analyse mislukt."
        );
      }

      applyProfileImport(json?.data || json || {});
    } catch (err) {
      setProfileImportError(
        err?.message || "Functieprofiel-analyse mislukt."
      );
      setProfileImportMessage("");
      setProfileImportResultOpen(true);
    } finally {
      setProfileImportBusy(false);
      if (e?.target) e.target.value = "";
    }
  }

  function openVacancyFromApplication() {
    const applicationId = form.id || selectedId;

    if (!applicationId) {
      alert("Sla de aanvraag eerst op voordat je er een vacature van maakt.");
      return;
    }

    const params = new URLSearchParams();
    params.set("application_id", String(applicationId));
    params.set("new", "1");

    try {
      sessionStorage.setItem("staffiq_active_module", "HRM");
      localStorage.setItem("staffiq_active_module", "HRM");
    } catch {}

    window.location.href = `/vacatures?${params.toString()}`;
  }

  function openMatchingPersoneelFromApplication() {
    const applicationId = form.id || selectedId;

    if (!applicationId) {
      alert("Sla de aanvraag eerst op voordat je personeel gaat matchen.");
      return;
    }

    const params = new URLSearchParams();
    params.set("application_id", String(applicationId));

    if (safeTrim(form.job_title || form.title)) params.set("functie", safeTrim(form.job_title || form.title));
    if (safeTrim(form.region)) params.set("regio", safeTrim(form.region));
    if (safeTrim(form.place)) params.set("plaats", safeTrim(form.place));
    if (safeTrim(form.driving_license)) params.set("rijbewijs", safeTrim(form.driving_license));
    if (safeTrim(form.experience)) params.set("ervaring", safeTrim(form.experience));
    if (safeTrim(form.own_transport)) params.set("eigenVervoer", safeTrim(form.own_transport));
    params.set("inzetVast", form.employment_vast !== false ? "1" : "0");
    params.set("inzetTijdelijk", form.employment_temporary !== false ? "1" : "0");
    params.set("inzetZZP", form.employment_zzp !== false ? "1" : "0");
    params.set("inzetDoorleen", form.employment_doorleen !== false ? "1" : "0");
    if (safeTrim(form.matching_text)) params.set("matchingGegevens", safeTrim(form.matching_text));

    // Wildcard krijgt vanuit Aanvragen altijd uitsluitend de functie / vacaturetitel.
    const searchText = safeTrim(form.job_title || form.title || "");
    if (searchText) params.set("q", searchText);

    window.location.href = `/matching-personeel?${params.toString()}`;
  }

  async function saveItem() {
    if (isReadonlyRole) return;

    const hasEmploymentType =
      form.employment_vast !== false ||
      form.employment_temporary !== false ||
      form.employment_zzp !== false ||
      form.employment_doorleen !== false;

    if (!hasEmploymentType) {
      const msg = "Kies minimaal één inzetvorm: Vast, Tijdelijk, ZZP of Doorleen.";
      setSaveError(msg);
      alert(msg);
      return;
    }

    const payload = {
      ...form,
      order_id: form.order_id ? Number(form.order_id) : null,
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      company_code_id: form.company_code_id ? Number(form.company_code_id) : null,
      number_of_people: form.number_of_people ? Number(form.number_of_people) : null,
      hours_per_week: form.hours_per_week === "" ? null : parseDecimal(form.hours_per_week),
      factor: form.factor === "" ? null : parseDecimal(form.factor),
      rate_indication: form.rate_indication === "" ? null : parseDecimal(form.rate_indication),
      sales_rate: form.sales_rate === "" ? null : parseDecimal(form.sales_rate),
      min_rate: form.min_rate === "" ? null : parseDecimal(form.min_rate),
      own_transport: form.company_transport === true ? "" : (form.own_transport ? "Ja" : ""),
      company_transport: form.company_transport === true,
      employment_vast: form.employment_vast !== false,
      employment_temporary: form.employment_temporary !== false,
      employment_zzp: form.employment_zzp !== false,
      employment_doorleen: form.employment_doorleen !== false,
      travel_costs: form.travel_costs === "" ? null : parseDecimal(form.travel_costs),
      travel_costs_unit: form.travel_costs_unit === "day" ? "day" : "km",
      travel_hours_amount: form.travel_hours_amount === "" ? null : parseDecimal(form.travel_hours_amount),
      active: form.active !== false,
    };

    try {
      setSaveError(null);

      const saved = await tryFetchJson(
        form.id
          ? [`/api/applications/${form.id}`, `/api/aanvragen/${form.id}`]
          : ["/api/applications", "/api/aanvragen"],
        {
          method: form.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const normalized = normalizeApplication(saved);

      setItems((prev) => {
        const idx = prev.findIndex((x) => String(x.id) === String(normalized.id));
        if (idx === -1) return [normalized, ...prev];
        const copy = prev.slice();
        copy[idx] = normalized;
        return copy;
      });

      // Na Opslaan: rechterkolom leeg en cursor op de eerste gridregel.
      setSelectedId(null);
      setForm(emptyFormToday());
      setMode("search");
      setNoteEditor("");
      setListIndex(0);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            // Rechterkolom terug naar boven en leeg laten.
            rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });

            // Eerste gridregel zichtbaar maken.
            const firstRow = rowRefs.current?.[0];
            firstRow?.scrollIntoView?.({
              behavior: "auto",
              block: "nearest",
              inline: "nearest",
            });

            // Geen focus in het lege formulier.
            if (
              document.activeElement &&
              typeof document.activeElement.blur === "function"
            ) {
              document.activeElement.blur();
            }
          } catch {}
        });
      });

      setFlash("Aanvraag opgeslagen ✅");
      setTimeout(() => setFlash(""), 1000);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  async function deleteItem() {
    if (isReadonlyRole || !form.id) return;
    if (!window.confirm("Aanvraag verwijderen?")) return;

    try {
      await tryFetchJson([`/api/applications/${form.id}`, `/api/aanvragen/${form.id}`], { method: "DELETE" });
      setItems((prev) => prev.filter((x) => String(x.id) !== String(form.id)));
      setSelectedId(null);
      setForm(emptyFormToday());
      setMode("search");
      setListIndex(-1);
      setFlash("Aanvraag verwijderd ✅");
      setTimeout(() => setFlash(""), 1000);

      requestAnimationFrame(() => {
        try {
          rightScrollRef.current?.scrollTo?.({
            top: 0,
            left: 0,
            behavior: "auto",
          });
        } catch {}

        try {
          pageTopRef.current?.scrollIntoView?.({
            behavior: "auto",
            block: "start",
          });
        } catch {}

        try {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        } catch {}

        if (window.innerWidth > 768) {
          setTimeout(() => {
            try {
              searchRef.current?.focus?.({ preventScroll: true });
            } catch {
              searchRef.current?.focus?.();
            }
          }, 0);
        }
      });
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  function moveCursor(delta) {
    if (!sorted.length) return;
    let idx = listIndex;
    if (idx < 0) idx = 0;
    idx = Math.max(0, Math.min(sorted.length - 1, idx + delta));
    setListIndex(idx);

    requestAnimationFrame(() => {
      try {
        rowRefs.current[idx]?.scrollIntoView?.({ block: "nearest" });
      } catch {}
    });
  }

  function openCurrentRow() {
    const idx = listIndex >= 0 ? listIndex : 0;
    const row = sorted[idx];
    if (row) loadItem(row);
  }

  function getFocusableElements() {
    return focusOrderRefs
      .map((r) => r?.current)
      .filter(Boolean)
      .filter((el) => {
        try {
          if (el.disabled) return false;
          const rect = el.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        } catch {
          return false;
        }
      });
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : 0;

    const next = els[Math.max(0, Math.min(els.length - 1, idx + dir))];
    try {
      next?.focus?.();
      next?.select?.();
    } catch {}
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 5000,
    onCommand: (cmd) => {
      if (!cmd?.type) return false;

      if (cmd.type === "next") {
        if (mode === "form") focusNextField(+1);
        else moveCursor(+1);
        return true;
      }

      if (cmd.type === "prev") {
        if (mode === "form") focusNextField(-1);
        else moveCursor(-1);
        return true;
      }

      if (cmd.type === "new") return newItem(), true;
      if (cmd.type === "refresh") return vernieuwList(), true;
      if (cmd.type === "save") return saveItem(), true;
      if (cmd.type === "delete") return deleteItem(), true;
      if (cmd.type === "select") return openCurrentRow(), true;

      return false;
    },
    onDictation: (text) => {
      const raw = String(text || "").trim();
      if (!raw) return;

      setSpeechInfo(`🎙 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      const t = norm(raw);
      if (t === "volgende") return mode === "form" ? focusNextField(+1) : moveCursor(+1);
      if (t === "vorige" || t === "terug") return mode === "form" ? focusNextField(-1) : moveCursor(-1);
      if (t === "nieuw") return newItem();
      if (t === "vernieuw" || t === "vernieuwen") return vernieuwList();
      if (t === "opslaan") return saveItem();
      if (t === "selecteer" || t === "item" || t === "rechts") return openCurrentRow();
      if (t === "lijst" || t === "links") return setMode("search");

      if (mode === "search") {
        if (isEmptyWord(raw)) setQ("");
        else setQ(raw);
      }
    },
  });

  const isMobile = window.innerWidth <= 768;
  const RIGHT_WIDTH = isMobile ? "100%" : 320;
  const GAP = isMobile ? 18 : 14;
  const TABLE_MIN_WIDTH = 1390;
  const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

  const colWidths = {
    date: 90,
    relation: 150,
    title: 125,
    function: 115,
    status: 75,
    order: 110,
    cao: 75,
    wage: 90,
    invoice: 100,
    factor: 65,
    active: 55,
    companyCode: 115,
  };

  const searchInputStyle = {
    padding: "7px 10px",
    width: isMobile ? "100%" : 220,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const inputStyle = {
    display: "block",
    padding: "7px 10px",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const selectStyle = {
    display: "block",
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

  const buttonPrimary = (bg) => ({
    ...buttonStyle,
    background: bg,
    color: "white",
    border: "1px solid rgba(0,0,0,0.05)",
  });

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = { ...thBase, cursor: "pointer" };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const rowBgCursor = "#f3f7ff";
  const rowBgSelected = "#e6f0ff";
  const labelStyle = { fontSize: 13, color: "#333" };
  const sectionTitleStyle = {
    fontSize: 13,
    fontWeight: 700,
    opacity: 0.75,
    marginTop: 8,
  };

  function filterSelect(value, setValue, options, includeYesNo = false) {
    return (
      <select value={value} onChange={(e) => setValue(e.target.value)} style={selectStyle}>
        <option value="">(alle)</option>
        {includeYesNo ? (
          <>
            <option value="j">j</option>
            <option value="n">n</option>
          </>
        ) : (
          options.map((x) => (
            <option key={x || EMPTY_VAL} value={x || EMPTY_VAL}>
              {x || "(leeg)"}
            </option>
          ))
        )}
      </select>
    );
  }

  function sortToggle(key) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === "date" ? "desc" : "asc" };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function renderTextField(label, key, ref = null, type = "text", extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type={type}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            if (extra.onChange) {
              extra.onChange(e.target.value);
              return;
            }
            setForm({ ...form, [key]: e.target.value });
          }}
          style={inputStyle}
          placeholder={extra.placeholder || ""}
          list={extra.list || undefined}
        />
      </>
    );
  }

  function formatDecimalNumberValue(n, decimals = 2) {
    if (!Number.isFinite(n)) return "";

    return n.toLocaleString("nl-NL", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }

  function applyCalculatedSalesRate(nextForm) {
    const factorRaw = String(nextForm.factor || "").trim();
    const rateRaw = String(nextForm.rate_indication || "").trim();

    // Als één van beide velden ontbreekt, laten we Verkooptarief gewoon vrij.
    if (!factorRaw || !rateRaw) return nextForm;

    const factorValue = parseDecimal(factorRaw);
    const rateValue = parseDecimal(rateRaw);

    if (!Number.isFinite(factorValue) || !Number.isFinite(rateValue)) return nextForm;

    return {
      ...nextForm,
      sales_rate: formatDecimalNumberValue(factorValue * rateValue, 2),
    };
  }

  function handleMoneyBlur(key, decimals = 2, extra = {}) {
    setForm((prev) => {
      const raw = String(prev[key] || "").trim();
      let next = { ...prev };

      if (raw) {
        const n = parseDecimal(raw);
        if (Number.isFinite(n)) {
          next[key] = formatDecimalInputValue(prev[key], decimals);
        }
      }

      if (extra.autoCalculateSalesRate === true) {
        next = applyCalculatedSalesRate(next);
      }

      return next;
    });
  }

  function renderMoneyField(label, key, ref = null, decimals = 2, extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <input
          ref={ref}
          type="text"
          inputMode="decimal"
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => {
            const clean = cleanDecimalInputValue(e.target.value);
            setForm({ ...form, [key]: clean });
          }}
          onBlur={() => handleMoneyBlur(key, decimals, extra)}
          style={inputStyle}
          placeholder="0,00"
        />
      </>
    );
  }

  function renderDecimalField(label, key, ref = null, decimals = 2, extra = {}) {
    return renderMoneyField(label, key, ref, decimals, extra);
  }

  function renderSelectField(label, key, ref, options) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <select
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          style={selectStyle}
        >
          <option value="">-</option>
          {options.map((x) => (
            <option key={x} value={x}>{x}</option>
          ))}
        </select>
      </>
    );
  }

  function renderTextareaField(label, key, ref = null, minHeight = 70, extra = {}) {
    return (
      <>
        <label style={labelStyle}>{label}</label>
        <textarea
          ref={ref}
          value={form[key] || ""}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          onFocus={() => {
            if (extra.largeEditor !== true) return;

            if (ignoreNextNoteFocusRef.current === key) {
              ignoreNextNoteFocusRef.current = "";
              return;
            }

            openNoteEditor(key);
          }}
          style={{ ...inputStyle, minHeight, resize: "vertical" }}
        />
      </>
    );
  }

  function renderCheckbox(label, key) {
    return (
      <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
        <input
          type="checkbox"
          checked={form[key] === true}
          disabled={isFormLocked}
          onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
        />
        {label}
      </label>
    );
  }

  return (
    <div>
      {profileImportBusy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.35)",
              maxWidth: 380,
              width: "100%",
              textAlign: "center",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              AI is bezig...
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.5, color: "#333" }}>
              Het functieprofiel wordt gelezen en de aanvraag wordt zo goed mogelijk ingevuld.
              <br />
              Dit kan even duren.
            </div>
          </div>
        </div>
      )}

      {profileImportResultOpen && (profileImportMessage || profileImportError) && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            zIndex: 999998,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: "18px 22px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.30)",
              maxWidth: 440,
              width: "100%",
              fontFamily: "Arial, Helvetica, sans-serif",
            }}
          >
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                marginBottom: 10,
                color: profileImportError ? "crimson" : "#1f6b1f",
              }}
            >
              {profileImportError
                ? "Functieprofiel-analyse mislukt"
                : "Functieprofiel geanalyseerd"}
            </div>

            <div style={{ fontSize: 14, lineHeight: 1.5, color: "#333", whiteSpace: "pre-wrap" }}>
              {profileImportError || profileImportMessage}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  setProfileImportResultOpen(false);
                  setTimeout(() => {
                    try {
                      rightScrollRef.current?.scrollTo?.({ top: 0, behavior: "auto" });
                      dateRef.current?.focus?.();
                      dateRef.current?.select?.();
                    } catch {}
                  }, 0);
                }}
                style={buttonPrimary("#1f4e79")}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : `minmax(0, 1fr) ${RIGHT_WIDTH}px`,
          columnGap: GAP,
          rowGap: isMobile ? 22 : 0,
          alignItems: "start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            ref={pageTopRef}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 0,
              marginBottom: 10,
            }}
          >
            <h1 style={{ margin: 0 }}>
              Aanvragen <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
            </h1>

            <HelpButton
              topic="aanvragen"
              title="Help over Aanvragen"
            />
          </div>

          {!isMobile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "nowrap" }}>
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setMode("search");
                }}
                placeholder="Zoek..."
                style={searchInputStyle}
              />

              <div
                title={`Aantal regels in het grid: ${gridCount}`}
                style={{
                  minWidth: 52,
                  height: 32,
                  padding: "0 10px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  background: "#f7fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#1f4e79",
                  boxSizing: "border-box",
                  whiteSpace: "nowrap",
                }}
              >
                {gridCount}
              </div>

              <button
                onMouseDown={(e) => e.preventDefault()}
                onTouchStart={(e) => e.preventDefault()}
                onClick={() => voice.startOnce?.()}
                disabled={!voice.supported || voice.listening}
                style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
              >
                {voice.listening ? "🎤 Luistert..." : "🎤 Spreek"}
              </button>

              <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>
                ➕ Nieuw
              </button>

              <button onClick={vernieuwList} style={buttonStyle}>
                🔄 Vernieuw
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 58px", gap: 6, marginBottom: 8, alignItems: "center" }}>
                <input
                  ref={searchRef}
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setMode("search");
                  }}
                  placeholder="Zoek..."
                  style={{ ...searchInputStyle, width: "100%", minWidth: 0 }}
                />
                <div
                  title={`Aantal regels in het grid: ${gridCount}`}
                  style={{
                    height: 34,
                    minHeight: 34,
                    padding: "0 8px",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                    background: "#f7fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#1f4e79",
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    lineHeight: "1",
                  }}
                >
                  {gridCount}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
                <button onClick={() => voice.startOnce?.()} disabled={!voice.supported || voice.listening} style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}>
                  {voice.listening ? "🎤..." : "🎤 Spreek"}
                </button>
                <button onClick={newItem} disabled={isReadonlyRole} style={isReadonlyRole ? buttonDisabledStyle : buttonStyle}>➕ Nieuw</button>
                <button onClick={vernieuwList} style={buttonStyle}>🔄 Vernieuw</button>
              </div>
            </>
          )}

          {speechInfo && <div style={{ marginTop: 8, fontSize: 13, color: "#1f4e79" }}>{speechInfo}</div>}
          {flash && <div style={{ marginTop: 8, fontSize: 13, color: "#1f7a1f" }}>{flash}</div>}
          {error && <div style={{ marginTop: 8, color: "#b00020" }}>{error}</div>}

          <div
            style={{
              marginTop: 12,
              border: "1px solid #e5e5e5",
              borderRadius: 6,
              background: "white",
              overflow: "auto",
              maxHeight: isMobile ? "45vh" : `calc(100dvh - ${MID_BOTTOM_OFFSET}px)`,
            }}
          >
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: colWidths.date }} />
                <col style={{ width: colWidths.relation }} />
                <col style={{ width: colWidths.title }} />
                <col style={{ width: colWidths.function }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.order }} />
                <col style={{ width: colWidths.cao }} />
                <col style={{ width: colWidths.wage }} />
                <col style={{ width: colWidths.invoice }} />
                <col style={{ width: colWidths.factor }} />
                <col style={{ width: colWidths.active }} />
                <col style={{ width: colWidths.companyCode }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thClickable} onClick={() => sortToggle("date")}>Aanvraagdatum{sortArrow("date")}</th>
                  <th style={thClickable} onClick={() => sortToggle("relation")}>Relatie{sortArrow("relation")}</th>
                  <th style={thClickable} onClick={() => sortToggle("title")}>Korte omschrijving{sortArrow("title")}</th>
                  <th style={thClickable} onClick={() => sortToggle("function")}>Functie{sortArrow("function")}</th>
                  <th style={thClickable} onClick={() => sortToggle("status")}>Status{sortArrow("status")}</th>
                  <th style={thClickable} onClick={() => sortToggle("order")}>Opdracht{sortArrow("order")}</th>
                  <th style={thClickable} onClick={() => sortToggle("cao")}>CAO{sortArrow("cao")}</th>
                  <th style={thClickable} onClick={() => sortToggle("wage")}>Loonperiode{sortArrow("wage")}</th>
                  <th style={thClickable} onClick={() => sortToggle("invoice")}>Factuurperiode{sortArrow("invoice")}</th>
                  <th style={thClickable} onClick={() => sortToggle("factor")}>Factor{sortArrow("factor")}</th>
                  <th style={thClickable} onClick={() => sortToggle("active")}>Actief{sortArrow("active")}</th>
                  <th style={thClickable} onClick={() => sortToggle("companyCode")}>Bedrijfscode{sortArrow("companyCode")}</th>
                </tr>
                <tr>
                  <th style={filterThStyle}>{filterSelect(dateFilter, setDateFilter, dateOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(relationFilter, setRelationFilter, relationOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(titleFilter, setTitleFilter, titleOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(functionFilter, setFunctionFilter, functionOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(statusFilter, setStatusFilter, statusOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(orderFilter, setOrderFilter, orderOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(caoFilter, setCaoFilter, caoOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(wagePeriodFilter, setWagePeriodFilter, wageOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(invoicePeriodFilter, setInvoicePeriodFilter, invoiceOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(factorFilter, setFactorFilter, factorOptions)}</th>
                  <th style={filterThStyle}>{filterSelect(activeFilter, setActiveFilter, [], true)}</th>
                  <th style={filterThStyle}>{filterSelect(companyCodeFilter, setCompanyCodeFilter, companyCodeOptions)}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={12} style={tdStyle}>Laden...</td></tr>
                ) : sorted.length === 0 ? (
                  <tr><td colSpan={12} style={tdStyle}>Geen aanvragen gevonden.</td></tr>
                ) : (
                  sorted.map((item, idx) => {
                    const isSelected = String(item.id) === String(selectedId);
                    const isCursor = idx === listIndex;
                    return (
                      <tr
                        key={item.id || idx}
                        ref={(el) => (rowRefs.current[idx] = el)}
                        onClick={() => {
                          setListIndex(idx);
                          loadItem(item);
                        }}
                        style={{
                          background: isSelected ? rowBgSelected : isCursor ? rowBgCursor : "white",
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle}>{formatDateNL(item.application_date)}</td>
                        <td style={tdStyle} title={relationName(item)}>{relationName(item)}</td>
                        <td style={tdStyle} title={item.title}>{item.title}</td>
                        <td style={tdStyle} title={item.job_title}>{item.job_title}</td>
                        <td style={tdStyle}>{item.status}</td>
                        <td style={tdStyle} title={orderReference(item)}>{orderReference(item) || "-"}</td>
                        <td style={tdStyle}>{item.cao}</td>
                        <td style={tdStyle}>{item.wage_period}</td>
                        <td style={tdStyle}>{item.invoice_period}</td>
                        <td style={tdStyle}>{decimalInput(item.factor)}</td>
                        <td style={tdStyle}>{boolToJaNee(parseActiveValue(item) !== false)}</td>
                        <td style={tdStyle} title={companyCodeName(item)}>{companyCodeName(item) || "-"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div
          ref={rightScrollRef}
          style={{
            width: isMobile ? "100%" : RIGHT_WIDTH,
            minWidth: isMobile ? 0 : RIGHT_WIDTH,
            maxWidth: isMobile ? "100%" : RIGHT_WIDTH,
            position: isMobile ? "relative" : "sticky",
            top: isMobile ? "auto" : 0,
            alignSelf: "start",
            maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
            overflowY: isMobile ? "visible" : "auto",
            background: "linear-gradient(180deg, #0b4f7a 0%, #083b5c 100%)",
            borderRadius: 10,
            padding: 10,
            paddingBottom: isMobile ? 34 : 10,
            boxSizing: "border-box",
            marginTop: isMobile ? 6 : 0,
            marginBottom: isMobile ? 34 : 0,
            paddingTop: isMobile ? 6 : 10,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: 10,
              padding: 12,
              paddingBottom: isMobile ? 28 : 12,
              boxSizing: "border-box",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: isMobile ? "visible" : "hidden",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 18 }}>
                  {noteEditor === "internal_note"
                    ? "Opmerking intern"
                    : noteEditor === "external_note"
                      ? "Opmerking extern"
                      : "Aanvraag"}
                </h2>

                {noteEditor ? (
                  <button
                    type="button"
                    onClick={closeNoteEditor}
                    style={buttonStyle}
                    title="Terug naar normaal formulier"
                  >
                    ↩ Terug
                  </button>
                ) : (
                  <span style={{ fontSize: 12, opacity: 0.55 }}>
                    {mode === "form" ? (form.id ? `#${form.id}` : "nieuw") : "geen selectie"}
                  </span>
                )}
              </div>

              {!noteEditor && (
                <>
                  <input
                    ref={profileFileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.rtf,image/*"
                    style={{ display: "none" }}
                    onChange={handleProfileImportFileChange}
                  />

                  <button
                    type="button"
                    onClick={() => profileFileInputRef.current?.click?.()}
                    disabled={isFormLocked || profileImportBusy}
                    style={{
                      ...buttonStyle,
                      marginTop: 8,
                      padding: "7px 8px",
                      fontSize: 14,
                      height: 32,
                      width: "100%",
                      background: "white",
                      color: "#1f4e79",
                      opacity: isFormLocked || profileImportBusy ? 0.5 : 1,
                      cursor: isFormLocked || profileImportBusy ? "not-allowed" : "pointer",
                    }}
                    title="Kies een PDF, Word-bestand, afbeelding of tekstbestand met een functieprofiel. De AI vult alleen alvast gegevens in; controleer alles voordat je opslaat."
                  >
                    📄 Functieprofiel importeren
                  </button>
                </>
              )}
            </div>

            {saveError && <div style={{ marginBottom: 10, color: "#b00020", fontSize: 13 }}>{saveError}</div>}

            {noteEditor ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateRows: "auto minmax(0, 1fr) auto",
                  gap: 10,
                  height: isMobile ? "auto" : "calc(100vh - 120px)",
                  maxHeight: isMobile ? "none" : "calc(100vh - 120px)",
                  minHeight: 0,
                }}
              >
                <div style={{ fontSize: 13, opacity: 0.8 }}>
                  Opmerking-editor (spreek: door-dicteren) — zeg <b>punt</b> voor “.”.
                </div>

                <textarea
                  ref={noteEditor === "internal_note" ? internalNoteBigRef : externalNoteBigRef}
                  value={form[noteEditor] || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, [noteEditor]: e.target.value })}
                  rows={15}
                  style={{
                    padding: "10px 10px",
                    width: "100%",
                    height: isMobile ? "50vh" : "100%",
                    minHeight: isMobile ? 320 : 0,
                    maxHeight: isMobile ? "60vh" : "100%",
                    border: "1px solid #d9d9d9",
                    borderRadius: 6,
                    background: "white",
                    resize: "vertical",
                    boxSizing: "border-box",
                    fontSize: 14,
                    lineHeight: 1.4,
                  }}
                />

                <div
                  style={{
                    background: "white",
                    paddingTop: 10,
                    marginTop: 6,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 10,
                    borderTop: "1px solid #eee",
                  }}
                >
                  <button
                    onClick={saveItem}
                    disabled={isFormLocked}
                    style={{
                      ...(isFormLocked
                        ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                        : buttonPrimary("#2f7dd1")),
                      width: "100%",
                    }}
                  >
                    💾 Opslaan
                  </button>

                  <button
                    onClick={deleteItem}
                    disabled={isReadonlyRole || !form.id}
                    style={{
                      ...(isReadonlyRole || !form.id
                        ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                        : buttonPrimary("#d64545")),
                      width: "100%",
                    }}
                  >
                    🗑 Verwijderen
                  </button>
                </div>
              </div>
            ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
              <div style={sectionTitleStyle}>Algemeen</div>
              {renderTextField("Aanvraagdatum", "application_date", dateRef, "date")}
              {renderTextField("Aanvraagreferentie", "application_number", numberRef)}

              <label style={labelStyle}>Opdracht</label>
              <select
                ref={orderRef}
                value={form.order_id || ""}
                disabled={isFormLocked}
                onChange={(e) => applyOrderDefaults(e.target.value)}
                style={selectStyle}
              >
                <option value="">-</option>
                {(orders || [])
                  .filter((o) => o?.active !== false)
                  .map((o) => {
                    const reference = safeTrim(o?.order_reference);
                    const relation = safeTrim(o?.organisation_name);
                    const label = [reference, relation].filter(Boolean).join(" - ") || `Opdracht #${o?.id}`;
                    return <option key={o.id} value={o.id}>{label}</option>;
                  })}
              </select>

              <label style={labelStyle}>Relatie</label>
              <select
                ref={relationRef}
                value={form.organisation_id || ""}
                disabled={isFormLocked}
                onChange={(e) => {
                  const organisationId = e.target.value;
                  const org = (organisations || []).find((o) => String(orgIdOf(o)) === String(organisationId));
                  setForm((prev) => ({
                    ...prev,
                    organisation_id: organisationId,
                    contact_id: "",
                    company_code_id: orgCompanyCodeIdFromList(organisations, organisationId),
                    company_code_name: orgCompanyCodeNameFromList(organisations, organisationId),
                    location: safeTrim(prev.location) || organisationVisitAddressText(org),
                    place: safeTrim(prev.place) || organisationVisitPlace(org),
                    region: safeTrim(prev.region) || organisationVisitRegion(org),
                  }));
                }}
                style={selectStyle}
              >
                <option value="">-</option>
                {organisations.map((o) => {
                  const id = orgIdOf(o);
                  const name = pickOrgName(o);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Contactpersoon</label>
              <select
                ref={contactRef}
                value={form.contact_id || ""}
                disabled={isFormLocked || !form.organisation_id}
                onChange={(e) => setForm({ ...form, contact_id: e.target.value })}
                style={selectStyle}
              >
                <option value="">-</option>
                {contacts.map((c) => {
                  const id = contactIdOf(c);
                  const name = pickContactName(c);
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>

              <label style={labelStyle}>Bedrijfscode</label>
              <input
                type="text"
                value={applicationCompanyCodeName(form, organisations)}
                disabled
                readOnly
                style={{ ...inputStyle, background: "#f7f7f7" }}
              />


              {renderTextField("Functie / vacaturetitel", "job_title", functionRef)}
              {renderTextField("Korte omschrijving", "title", titleRef)}
              {renderTextField("Aantal personen", "number_of_people", peopleRef, "number")}
              {renderSelectField("Status", "status", statusRef, ["Concept", "Open", "In behandeling", "Kandidaten voorgesteld", "Gedeeltelijk ingevuld", "Ingevuld", "On hold", "Vervallen", "Geannuleerd"])}
              {renderTextareaField("Omschrijving werkzaamheden", "work_description", workDescriptionRef, 80)}

              <div style={sectionTitleStyle}>Periode / locatie</div>
              {renderTextField("Startdatum", "start_date", startDateRef, "date")}
              {renderTextField("Einddatum", "end_date", endDateRef, "date")}
              {renderTextField("Opzegtermijn", "notice_period", noticePeriodRef)}
              {renderDecimalField("Uren per week", "hours_per_week", hoursRef, 2)}
              {renderTextareaField("Werktijden / roostertekst", "schedule_text", scheduleRef, 70)}
              {renderTextareaField("Locatie / bezoekadres", "location", locationRef, 96)}
              {renderTextField("Contact op locatie", "location_contact", locationContactRef)}
              {renderTextareaField("Aanmeldprocedure", "registration_procedure", registrationProcedureRef, 70)}
              {renderTextField("Regio", "region", regionRef)}

              <div style={sectionTitleStyle}>Eisen / profiel</div>
              {renderTextField("Opleiding", "education", educationRef)}
              {renderTextField("Werkervaring", "experience", experienceRef)}
              {renderTextField("Certificaten", "certificates", certificatesRef)}
              {renderTextField("Rijbewijs", "driving_license", drivingLicenseRef)}
              <div style={{ marginBottom: 9 }}>
                <label style={{ display: "block", fontSize: 12, color: "#555", marginBottom: 5 }}>
                  Vervoer
                </label>
                <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13 }}>
                    <input
                      ref={ownTransportRef}
                      type="checkbox"
                      checked={safeTrim(form.own_transport).toLowerCase() === "ja"}
                      disabled={isFormLocked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          own_transport: e.target.checked ? "Ja" : "",
                          company_transport: e.target.checked ? false : prev.company_transport,
                        }))
                      }
                    />
                    Eigen
                  </label>

                  <label style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={form.company_transport === true}
                      disabled={isFormLocked}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          company_transport: e.target.checked,
                          own_transport: e.target.checked ? "" : prev.own_transport,
                        }))
                      }
                    />
                    Vd Zaak
                  </label>
                </div>
              </div>

              <div style={{ marginBottom: 9 }}>
                <label style={{ display: "block", fontSize: 12, color: "#555", marginBottom: 5 }}>
                  Inzetvorm
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "7px 14px",
                    alignItems: "center",
                  }}
                >
                  {[
                    ["employment_vast", "Vast"],
                    ["employment_temporary", "Tijdelijk"],
                    ["employment_zzp", "ZZP"],
                    ["employment_doorleen", "Doorleen"],
                  ].map(([key, label]) => (
                    <label
                      key={key}
                      style={{
                        display: "flex",
                        gap: 7,
                        alignItems: "center",
                        fontSize: 13,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={form[key] !== false}
                        disabled={isFormLocked}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            [key]: e.target.checked,
                          }))
                        }
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <div style={{ marginTop: 5, fontSize: 11, color: "#777" }}>
                  Meerdere keuzes zijn mogelijk. Minimaal één inzetvorm is verplicht.
                </div>
              </div>

              {renderTextField("Talen", "languages", languagesRef)}
              {renderTextareaField("Matchingtekst / profiel", "matching_text", matchingTextRef, 90)}

              <div style={sectionTitleStyle}>Tarieven / afspraken</div>
              {renderTextField("CAO", "cao", caoRef)}
              {renderSelectField("Loonperiode", "wage_period", wagePeriodRef, ["week", "4-weken", "maand"])}
              {renderSelectField("Factuurperiode", "invoice_period", invoicePeriodRef, ["week", "4-weken", "maand"])}
              {renderDecimalField("Factor", "factor", factorRef, 2, { autoCalculateSalesRate: true })}
              {renderMoneyField("Salarisindicatie", "rate_indication", rateIndicationRef, 2, { autoCalculateSalesRate: true })}
              {renderMoneyField("Verkooptarief", "sales_rate", salesRateRef)}
              {renderMoneyField("Minimaal tarief", "min_rate", minRateRef)}
              <label style={labelStyle}>Reiskosten</label>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 90px", gap: 6 }}>
                <input
                  ref={travelCostsRef}
                  type="text"
                  inputMode="decimal"
                  value={form.travel_costs || ""}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, travel_costs: cleanDecimalInputValue(e.target.value) })}
                  onBlur={() => handleMoneyBlur("travel_costs", 2)}
                  style={inputStyle}
                  placeholder="0,00"
                />
                <select
                  ref={travelCostsUnitRef}
                  value={form.travel_costs_unit || "km"}
                  disabled={isFormLocked}
                  onChange={(e) => setForm({ ...form, travel_costs_unit: e.target.value })}
                  style={selectStyle}
                >
                  <option value="km">per km</option>
                  <option value="day">per dag</option>
                </select>
              </div>
              {renderMoneyField("Reisuren per dag", "travel_hours_amount", travelHoursAmountRef, 2)}
              {renderTextareaField("Toeslagen", "allowances", allowancesRef, 60)}
              {renderTextareaField("Factuurafspraak", "invoice_agreement", invoiceAgreementRef, 70)}

              <div style={sectionTitleStyle}>Proces</div>
              {renderSelectField("Prioriteit", "priority", priorityRef, ["Laag", "Normaal", "Hoog", "Spoed"])}
              {renderTextField("Bron aanvraag", "request_source", sourceRef)}
              {renderTextField("Deadline voorstellen", "proposal_deadline", proposalDeadlineRef, "date")}

              <div style={sectionTitleStyle}>Opmerkingen</div>
              {renderTextareaField("Opmerking intern", "internal_note", internalNoteRef, 80, { largeEditor: true })}
              {renderTextareaField("Opmerking extern", "external_note", externalNoteRef, 80, { largeEditor: true })}
              {renderCheckbox("Actief", "active")}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 8,
                  marginTop: 8,
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <button
                  onClick={openVacancyFromApplication}
                  disabled={!form.id && !selectedId}
                  style={{
                    ...(!form.id && !selectedId
                      ? { ...buttonPrimary("#7c3aed"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#7c3aed")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                  title="Maak optioneel een vacature vanuit deze aanvraag"
                >
                  📣 Vacature maken
                </button>

                <button
                  onClick={openMatchingPersoneelFromApplication}
                  disabled={!form.id && !selectedId}
                  style={{
                    ...(!form.id && !selectedId
                      ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#198754")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                  title="Ga rechtstreeks vanuit de aanvraag naar Matching Personeel"
                >
                  🧩 Match personeel
                </button>

                <button
                  onClick={saveItem}
                  disabled={isReadonlyRole || mode !== "form"}
                  style={{
                    ...(isReadonlyRole || mode !== "form"
                      ? { ...buttonPrimary("#2f7dd1"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#2f7dd1")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  💾 Opslaan
                </button>

                <button
                  onClick={deleteItem}
                  disabled={isReadonlyRole || !form.id}
                  style={{
                    ...(isReadonlyRole || !form.id
                      ? { ...buttonPrimary("#d64545"), opacity: 0.5, cursor: "not-allowed" }
                      : buttonPrimary("#d64545")),
                    width: "100%",
                    minWidth: 0,
                    height: 32,
                    padding: "7px 8px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  🗑 Verwijderen
                </button>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
